/*
 *  -----------------------------------------------------------------------
 *    <copyright file="login-step2.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { gql } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { ApiLogin2FA, Resend2FaCode } from 'apis/login';
import { FeatureContext } from 'contexts/feature-context';
import { UserContext } from 'contexts/user-context';
import { useBoolean, useStateful } from 'hooks';
import { validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons/button';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { DialogActions } from 'views/components/core/dialog';
import DialogInformationBox from 'views/components/core/dialog-information-box/dialog-information-box';
import FormControl from 'views/components/core/form-control';
import Loading from 'views/components/core/loading';
import TextField from 'views/components/core/text-field';
import 'views/layouts/login/login.scss';
import './uam-styles.scss';
import { AllRoutes } from '@utils/router/all-routes';

const LoginStep2 = () => {
    const { hasFeature } = useContext(FeatureContext);
    const { user, token: loginToken, setToken } = useContext(UserContext);
    const navigate = useNavigate();
    const { token, phone, orgCode } = useParams();
    const { t } = useTranslation();
    const [code, setCode] = useState('');
    const [failedAttempts, setFailedAttempts] = useState(0);
    const loading = useBoolean(false);
    const isResendDisabled = useBoolean(false);
    const error = useStateful('');
    const maxLength = 6;
    const timeout = useRef(null);
    const [resendError, setResendError] = useState(null);

    const [getResetToken, { data: resetToken, loading: loadingResetToken, error: resetTokenError }] = useLazyQuery(gql`
        query passwordResetToken {
            passwordResetToken
        }
    `);

    useEffect(() => {
        if (user && loginToken) {
            if (user.forceResetPassword) {
                if (resetToken?.passwordResetToken)
                    if (!user.isAdmin) {
                        navigate(
                            AllRoutes.path('password-reset-org', { token: resetToken?.passwordResetToken, orgCode }),
                        );
                    } else {
                        navigate(AllRoutes.path('password-reset', { token: resetToken?.passwordResetToken }));
                    }
                else if (!loadingResetToken) getResetToken();
            } else navigate(AllRoutes.path('home'));
        }
    }, [user, loginToken, navigate, resetToken]);

    useEffect(() => {
        configureTimeout();
    }, []);

    useEffect(() => {
        if (failedAttempts === 2)
            navigate(AllRoutes.path(orgCode ? 'login-organisation' : 'login', orgCode ? { orgCode } : null), {
                replace: true,
            });
    }, [failedAttempts]);

    const configureTimeout = () => {
        if (timeout.current) {
            clearTimeout(timeout.current);
            timeout.current = null;
        } else {
            timeout.current = setTimeout(() => {
                navigate(AllRoutes.path(orgCode ? 'login-organisation' : 'login', orgCode ? { orgCode } : null), {
                    replace: true,
                });
            }, 600000);
        }
    };

    const submitLogin = async (e: any) => {
        e.preventDefault();
        loading.setTrue();
        const response = await ApiLogin2FA(token, code);
        if (response) loading.setFalse();
        if (response.token) configureTimeout();
        if (response.error) {
            if (response.error.match('TokenExpired')) error.setValue(t('login:digitCodeHasExpired'));
            else {
                if (response.error.match('login2FAIncorrect')) setFailedAttempts(failedAttempts + 1);
                else if (response.error.match('loginTooManyFailedLogins'))
                    navigate(AllRoutes.path(orgCode ? 'login-organisation' : 'login', orgCode ? { orgCode } : null), {
                        replace: true,
                    });
                error.setValue(
                    response.error === 'Failed to fetch'
                        ? 'Unable to contact the server. Please try again later.'
                        : t(response.error),
                );
            }
            return;
        }

        // Set their token which will take them in
        setToken(response.token);
    };

    if (loading.value || loadingResetToken) return <Loading />;

    const onChangeCode = (event: any) => {
        setCode(event.target.value);

        const validationError = validateFields(event.target.value, 'code', maxLength, true);

        if (validationError.code) {
            error.setValue(validationError.code);
        } else {
            error.setValue('');
        }
    };

    const resendLinkClick = async () => {
        isResendDisabled.setTrue();
        const response = await Resend2FaCode(token);
        if (response.error) {
            setResendError(response.error);
        }
    };

    return (
        <div className="login-page">
            <h1 className="login-title">{`${t('login:towStepVerification')}`}</h1>

            <DialogInformationBox topText={t('login:aTextMessageWithYourCodeHasBeenSent', { last4Numbers: phone })} />

            <form className="login-page-form error" onSubmit={submitLogin}>
                <FormControl className="form-control" fullWidth required style={{ marginBottom: 35 }}>
                    <TextField
                        error={!!error.value}
                        label="6-Digit Code*"
                        id="code"
                        type="number"
                        autoFocus
                        fullWidth
                        value={code}
                        onChange={onChangeCode}
                        placeholder="6-Digit Code*"
                        variant="outlined"
                        color="secondary"
                        helperText={
                            <CharactersRemaining
                                id="login-code"
                                length={code?.length}
                                max={maxLength}
                                error={error.value}
                            />
                        }
                    />
                </FormControl>

                <DialogActions
                    style={{ paddingTop: 20, paddingBottom: 10 }}
                    className="actions-info-wrapper-login-page flex-end"
                >
                    <Button disabled={isResendDisabled.value} className="link" onClick={resendLinkClick}>
                        {t('login:resendCode')}
                    </Button>

                    <Button
                        className="login-submit-button"
                        disabled={loading.value || code?.length !== maxLength}
                        text="login:verify"
                        id="login-verify"
                        type="submit"
                    />
                </DialogActions>

                <div className="error-notification">{resendError ? t(resendError) : null}</div>
            </form>
        </div>
    );
};

export default LoginStep2;
